import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import COLORS from '@/resources/style/Themes';
import { StringKeys } from '@/resources/strings/StringKeys';
import { OrganizationOnAzure, SubscriptionTier } from '../../../../generated-client/models';
import Group from '@/models/Organization/Group';
import { OrgUser } from '@/models/User/OrgUser';
import { flattenAllOrgUsers } from '@/utils/organization/allUsersOverview';
import {
  buildPoolCounts,
  getPoolPurchased,
  getPoolRemaining,
  getPoolUsage,
} from '@/utils/organization/orgSeatAvailability';
import SubscriptionBreakdownOverlay, {
  type SubscriptionPoolSummary,
} from './SubscriptionBreakdownOverlay';
import UserTable from './UserTable';

type AllUsersPageProps = {
  allGroups: Group[];
  organization: OrganizationOnAzure;
  orgUser: OrgUser | null;
  userToHighlight: string | null;
  setUserToHighlight: Dispatch<SetStateAction<string | null>>;
  setConfirmTitle: (title: string) => void;
  setConfirmMessage: (message: string) => void;
  setSelectedUser: (user: OrgUser) => void;
  setShowConfirmModal: (show: boolean) => void;
};

const ALL_USERS_INITIAL_PAGE_SIZE = 15;
const ALL_USERS_PAGE_SIZE_OPTIONS = [15, 25, 50];
const SHORT_VIEWPORT_QUERY = '(max-height: 1100px)';

function buildPaidPoolSummary(
  organization: OrganizationOnAzure,
): SubscriptionPoolSummary {
  const poolCounts = buildPoolCounts(organization);
  const toRow = (tier: SubscriptionTier) => ({
    purchased: getPoolPurchased(poolCounts, tier),
    used: getPoolUsage(poolCounts, tier),
    remaining: getPoolRemaining(poolCounts, tier),
  });
  return {
    ops: toRow(SubscriptionTier.Ops),
    respond: toRow(SubscriptionTier.Respond),
  };
}

const AllUsersPage = ({
  allGroups,
  organization,
  orgUser,
  userToHighlight,
  setUserToHighlight,
  setConfirmTitle,
  setConfirmMessage,
  setSelectedUser,
  setShowConfirmModal,
}: AllUsersPageProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isShortViewport = useMediaQuery(SHORT_VIEWPORT_QUERY);
  const [showSubscriptionBreakdown, setShowSubscriptionBreakdown] = useState(false);
  const poolSummary = useMemo(
    () => buildPaidPoolSummary(organization),
    [organization],
  );

  const users = useMemo(() => flattenAllOrgUsers(allGroups), [allGroups]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: isMobile ? undefined : 1,
        minHeight: 0,
        overflow: isMobile ? undefined : 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mt: isShortViewport ? 1.5 : 3,
          mb: isShortViewport ? 1 : 2,
          flexShrink: 0,
        }}
      >
        <Box sx={{ position: 'relative', display: 'inline-block' }}>
          <Typography
            sx={{
              position: 'absolute',
              top: -12,
              ml: '2px',
              fontWeight: 500,
              fontSize: 15,
              color: COLORS.TEXT_HEADER_GRAY,
              textTransform: 'capitalize',
              lineHeight: 1,
            }}
          >
            {t(StringKeys.ALL_USERS)}
          </Typography>
          <Typography
            sx={{
              color: COLORS.SAFEGUARD_BLUE,
              fontWeight: 400,
              fontSize: 28,
            }}
          >
            {organization.company_name}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => setShowSubscriptionBreakdown(true)}
        >
          {t(StringKeys.VIEW_SUBSCRIPTIONS_BREAKDOWN)}
        </Button>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: isMobile ? undefined : 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: isMobile ? undefined : 'hidden',
        }}
      >
        <UserTable
          users={users}
          userToHighlight={userToHighlight}
          setUserToHighlight={setUserToHighlight}
          groupName=""
          organization={organization}
          orgUser={orgUser}
          setConfirmTitle={setConfirmTitle}
          setConfirmMessage={setConfirmMessage}
          setSelectedUser={setSelectedUser}
          setShowConfirmModal={setShowConfirmModal}
          allGroups={allGroups}
          initialPageSize={ALL_USERS_INITIAL_PAGE_SIZE}
          pageSizeOptions={ALL_USERS_PAGE_SIZE_OPTIONS}
          gridHeight={isMobile ? undefined : '100%'}
          toolbarAlignRight
          quickFilterPlaceholder={t(
            StringKeys.SEARCH_WORKERS_BY_NAME_ROLE_SUBSCRIPTION_PLAN,
          )}
        />
      </Box>
      <SubscriptionBreakdownOverlay
        open={showSubscriptionBreakdown}
        onClose={() => setShowSubscriptionBreakdown(false)}
        allGroups={allGroups}
        companyName={organization.company_name ?? ''}
        poolSummary={poolSummary}
      />
    </Box>
  );
};

export default AllUsersPage;
